import {
  FontStyle,
  FontVariant,
  getSubsetUnicodeRange,
  getPrimaryFontFamily,
  getSelfHostedFont,
  resolveFontFamily,
} from "./font-manifest";

const loadedFontsByDocument = new WeakMap<Document, Map<string, Promise<void>>>();

const getClosestVariant = (
  variants: FontVariant[],
  weight: number,
  style: FontStyle,
): FontVariant | undefined => {
  const sameStyle = variants.filter((variant) => variant.style === style);
  const candidates = sameStyle.length ? sameStyle : variants.filter((variant) => variant.style === "normal");

  return candidates.sort((left, right) => Math.abs(left.weight - weight) - Math.abs(right.weight - weight))[0];
};

export const loadFont = async (
  family?: string,
  weight = 400,
  style: FontStyle = "normal",
  targetDocument: Document = document,
): Promise<void> => {
  const faceFamily = getPrimaryFontFamily(family);
  const resolvedFamily = resolveFontFamily(faceFamily);
  const font = getSelfHostedFont(resolvedFamily);
  if (!font || !faceFamily || typeof FontFace === "undefined") return;

  const variant = getClosestVariant(font.variants, weight, style);
  if (!variant) return;

  const cacheKey = `${faceFamily}:${variant.weight}:${variant.style}`;
  const loadedFonts = loadedFontsByDocument.get(targetDocument) || new Map<string, Promise<void>>();
  loadedFontsByDocument.set(targetDocument, loadedFonts);

  if (!loadedFonts.has(cacheKey)) {
    loadedFonts.set(
      cacheKey,
      Promise.allSettled(
        variant.assets.map(async (asset) => {
          const fontFace = new FontFace(
            faceFamily,
            `url(${asset.url}) format("woff2")`,
            {
              weight: String(variant.weight),
              style: variant.style,
              unicodeRange: getSubsetUnicodeRange(asset.subset),
            },
          );
          const loadedFace = await fontFace.load();
          (targetDocument.fonts as unknown as { add: (face: FontFace) => void }).add(loadedFace);
        }),
      ).then((): void => undefined),
    );
  }

  return loadedFonts.get(cacheKey)!;
};

export const loadFontFamily = async (
  family?: string,
  targetDocument: Document = document,
): Promise<void> => {
  await loadFont(family, 400, "normal", targetDocument);
};
